import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../components/Alert';
import { useConfirm } from '../components/Confirm';
import { useQuestionnairePrompt } from '../components/QuestionnairePrompt';
import { leagueAPI, matchAPI, userAPI, clubAPI, levelsAPI, messageAPI } from '../services/api';
import MapPicker from '../components/MapPicker';
import ChatBox from '../components/ChatBox';
import './LeaguePage.css';

const LeaguePage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin: isGlobalAdmin } = useAuth();
  const { showAlert } = useAlert();
  const { showConfirm } = useConfirm();
  const { showQuestionnairePrompt } = useQuestionnairePrompt();
  const [league, setLeague] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchPagination, setMatchPagination] = useState(null);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [showLocationMap, setShowLocationMap] = useState(true);
  const [showSubleagueStandings, setShowSubleagueStandings] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddMatch, setShowAddMatch] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [matchForm, setMatchForm] = useState({
    player1Id: '',
    player2Id: '',
    winnerId: '',
    score: [],
    matchDate: new Date().toISOString().split('T')[0],
    walkover: false,
    walkoverPlayer: null,
  });
  const [canCreateContinuation, setCanCreateContinuation] = useState(true);
  const [continuationLimitMessage, setContinuationLimitMessage] = useState('');
  const [showContinuationHistory, setShowContinuationHistory] = useState(false);
  const [checkingClubAccess, setCheckingClubAccess] = useState(false);
  const [clubAccessChecked, setClubAccessChecked] = useState(false);
  const [clubAccessDenied, setClubAccessDenied] = useState(false);
  const [chatBoxOpen, setChatBoxOpen] = useState(false);
  const [selectedChatUser, setSelectedChatUser] = useState(null);

  const blurInput = (target) => {
    if (typeof target?.blur === 'function') {
      requestAnimationFrame(() => target.blur());
    }
  };

  const formatLevel = (value) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return 0;
    }
    return Math.round(numeric);
  };

  const formatDateDisplay = (value) => {
    if (!value) return 'TBD';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'TBD';
    return parsed.toLocaleDateString();
  };

  const handleMatchDateChange = (e) => {
    const { value } = e.target;
    setMatchForm((prev) => ({ ...prev, matchDate: value }));
    blurInput(e.target);
  };

  const handleEditingMatchDateChange = (e) => {
    const { value } = e.target;
    setEditingMatch((prev) => (prev ? { ...prev, matchDate: value } : prev));
    blurInput(e.target);
  };

  const getMatchFormatDisplay = (format) => {
    const formats = {
      'best-of-3-sets-to-6': 'Best of 3 Sets (to 6)',
      'best-of-3-sets-to-4': 'Best of 3 Sets (to 4)',
      'best-of-3-deciding-tiebreak-sets-to-6': 'Best of 3 (3rd set tiebreak)',
      'best-of-3-deciding-tiebreak-sets-to-4': 'Best of 3 (3rd set tiebreak, sets to 4)',
      'single-set': 'Single Set',
      'squash-best-of-5-games-to-11': 'Best of 5 Games (First to 11, win by 2)',
      'squash-best-of-3-games-to-11': 'Best of 3 Games (First to 11, win by 2)',
    };
    return formats[format] || format;
  };

  const getScoreInputConfig = (format) => {
    const configs = {
      'best-of-3-sets-to-6': { sets: 3, maxScore: 7, hasTiebreak: false },
      'best-of-3-sets-to-4': { sets: 3, maxScore: 5, hasTiebreak: false },
      'best-of-3-deciding-tiebreak-sets-to-6': { sets: 3, maxScore: 7, hasTiebreak: true, tiebreakMax: 15 },
      'best-of-3-deciding-tiebreak-sets-to-4': { sets: 3, maxScore: 5, hasTiebreak: true, tiebreakMax: 15 },
      'single-set': { sets: 1, maxScore: 7, hasTiebreak: false },
      'squash-best-of-5-games-to-11': { sets: 5, maxScore: 11, hasTiebreak: false, isSquash: true },
      'squash-best-of-3-games-to-11': { sets: 3, maxScore: 11, hasTiebreak: false, isSquash: true },
    };
    return configs[format] || configs['best-of-3-sets-to-6'];
  };

  // Validate set score according to tennis or squash rules
  // For sets to 4: win by 2 with minimum 4 games, or 5-4
  // For sets to 6: win by 2 with minimum 6 games, or 7-6
  // For squash games to 11: win by 2 with minimum 11 points, points must be > 0
  const validateSetScore = (winnerScore, loserScore, maxScore, isSquash = false) => {
    if (isSquash && maxScore === 11) {
      // Squash: first to 11, win by 2, points must be > 0
      if (winnerScore <= 0 || loserScore <= 0) return false;
      if (winnerScore < 11) return false; // Must reach at least 11
      if (winnerScore === 11) {
        // At 11, must win by 2, so loser can have 0-9
        return loserScore <= 9 && (winnerScore - loserScore >= 2);
      }
      // Above 11, must win by 2
      return (winnerScore - loserScore >= 2);
    }
    
    // If maxScore is 5, it's sets to 4
    // If maxScore is 7, it's sets to 6
    const isSetTo4 = maxScore === 5;
    const isSetTo6 = maxScore === 7;
    
    if (isSetTo4) {
      // Sets to 4: winner needs at least 4, win by 2, or 5-4
      if (winnerScore < 4) return false;
      if (winnerScore === 4) {
        // Must win by 2, so loser can have 0-2
        return loserScore <= 2 && (winnerScore - loserScore >= 2);
      }
      if (winnerScore === 5 && loserScore === 4) return true; // 5-4 is valid
      if (winnerScore === 5 && loserScore === 3) return true; // 5-3 is valid
      if (winnerScore > 5) {
        // Must win by 2
        return (winnerScore - loserScore >= 2);
      }
      return false;
    } else if (isSetTo6) {
      // Sets to 6: winner needs at least 6, win by 2, or 7-6
      if (winnerScore < 6) return false;
      if (winnerScore === 6) {
        // Must win by 2, so loser can have 0-4
        return loserScore <= 4 && (winnerScore - loserScore >= 2);
      }
      if (winnerScore === 7 && (loserScore === 6 || loserScore === 5)) return true; // 7-6 or 7-5 is valid
      if (winnerScore > 7) {
        // Must win by 2
        return (winnerScore - loserScore >= 2);
      }
      return false;
    }
    
    // Default: must win by 2
    return winnerScore > loserScore && (winnerScore - loserScore >= 2);
  };

  // Validate tiebreak score: win by 2 with minimum 10 games
  const validateTiebreakScore = (winnerScore, loserScore) => {
    if (winnerScore < 10) return false;
    return (winnerScore - loserScore >= 2);
  };

  // Normalize score array: ensure all positions have numeric values (default to 0)
  const normalizeScoreArray = (scoreArray, matchFormat) => {
    if (!scoreArray || !Array.isArray(scoreArray)) return [];
    
    const config = getScoreInputConfig(matchFormat);
    const normalized = [...scoreArray];
    const isSquash = config.isSquash || false;
    
    if (config.sets === 1) {
      // Single set: need 2 values
      while (normalized.length < 2) {
        normalized.push(0);
      }
      return normalized.slice(0, 2).map(v => {
        const num = parseInt(v);
        return isNaN(num) ? 0 : num;
      });
    } else if ((config.sets === 5 || config.sets === 3) && isSquash) {
      // Best of 5 or Best of 3 games (Squash): need up to (sets * 2) values
      // Match can end early if someone wins required games (3 for best of 5, 2 for best of 3)
      const gamesToWin = config.sets === 5 ? 3 : 2;
      let p1GamesWon = 0;
      let p2GamesWon = 0;
      let gamesPlayed = 0;
      
      // Count games won to determine how many games were played
      for (let i = 0; i < Math.min(config.sets, normalized.length / 2); i++) {
        const p1Score = parseInt(normalized[i * 2]) || 0;
        const p2Score = parseInt(normalized[i * 2 + 1]) || 0;
        if (p1Score > 0 || p2Score > 0) {
          gamesPlayed++;
          if (p1Score > p2Score) p1GamesWon++;
          else if (p2Score > p1Score) p2GamesWon++;
        }
        if (p1GamesWon === gamesToWin || p2GamesWon === gamesToWin) break;
      }
      
      // Ensure we have enough values for all played games
      const requiredLength = gamesPlayed * 2;
      while (normalized.length < requiredLength) {
        normalized.push(0);
      }
      
      return normalized.slice(0, requiredLength).map(v => {
        const num = parseInt(v);
        return isNaN(num) ? 0 : num;
      });
    } else {
      // Best of 3: need at least 4 values (2 sets), and optionally 2 more for 3rd set/tiebreak
      // Always ensure first 4 positions are filled
      while (normalized.length < 4) {
        normalized.push(0);
      }
      
      // Check if match is tied 1-1, then ensure 5th and 6th positions exist
      const set1P1Score = parseInt(normalized[0]) || 0;
      const set1P2Score = parseInt(normalized[1]) || 0;
      const set2P1Score = parseInt(normalized[2]) || 0;
      const set2P2Score = parseInt(normalized[3]) || 0;
      
      const p1SetsWon = (set1P1Score > set1P2Score ? 1 : 0) + (set2P1Score > set2P2Score ? 1 : 0);
      const p2SetsWon = (set1P2Score > set1P1Score ? 1 : 0) + (set2P2Score > set2P1Score ? 1 : 0);
      
      const isTied = p1SetsWon === 1 && p2SetsWon === 1;
      
      if (isTied) {
        // If tied, ensure we have 6 values
        while (normalized.length < 6) {
          normalized.push(0);
        }
        return normalized.slice(0, 6).map(v => {
          const num = parseInt(v);
          return isNaN(num) ? 0 : num;
        });
      } else {
        // Match decided in 2 sets, only return first 4 values
        return normalized.slice(0, 4).map(v => {
          const num = parseInt(v);
          return isNaN(num) ? 0 : num;
        });
      }
    }
  };

  const calculateWinnerFromScores = (player1Id, player2Id, scoreArray, matchFormat) => {
    if (!scoreArray || scoreArray.length === 0) return null;
    
    const config = getScoreInputConfig(matchFormat);
    const isSquash = config.isSquash || false;
    
    if (config.sets === 1) {
      // Single set: validate and determine winner
      const p1Score = parseInt(scoreArray[0]) || 0;
      const p2Score = parseInt(scoreArray[1]) || 0;
      
      // Validate set score
      if (p1Score > p2Score) {
        if (!validateSetScore(p1Score, p2Score, config.maxScore, isSquash)) return null;
        return player1Id;
      }
      if (p2Score > p1Score) {
        if (!validateSetScore(p2Score, p1Score, config.maxScore, isSquash)) return null;
        return player2Id;
      }
      return null; // Draw or invalid
    } else if ((config.sets === 5 || config.sets === 3) && isSquash) {
      // Best of 5 or Best of 3 games (Squash): need to win required games
      const gamesToWin = config.sets === 5 ? 3 : 2;
      let p1GamesWon = 0;
      let p2GamesWon = 0;
      
      for (let i = 0; i < config.sets; i++) {
        if (i * 2 + 1 >= scoreArray.length) break;
        const p1Score = parseInt(scoreArray[i * 2]) || 0;
        const p2Score = parseInt(scoreArray[i * 2 + 1]) || 0;
        
        if (p1Score === 0 && p2Score === 0) break; // No more games
        
        if (p1Score > p2Score) {
          if (!validateSetScore(p1Score, p2Score, config.maxScore, isSquash)) return null;
          p1GamesWon++;
        } else if (p2Score > p1Score) {
          if (!validateSetScore(p2Score, p1Score, config.maxScore, isSquash)) return null;
          p2GamesWon++;
        } else {
          // Draw game - invalid
          return null;
        }
        
        // Match can end early if someone wins required games
        if (p1GamesWon === gamesToWin || p2GamesWon === gamesToWin) break;
      }
      
      if (p1GamesWon > p2GamesWon) return player1Id;
      if (p2GamesWon > p1GamesWon) return player2Id;
      return null; // Invalid or incomplete
    } else {
      // Best of 3: need to win 2 sets
      let p1SetsWon = 0;
      let p2SetsWon = 0;
      
      if (config.hasTiebreak) {
        // 2 sets + tiebreak
        for (let i = 0; i < 2; i++) {
          const p1Score = parseInt(scoreArray[i * 2]) || 0;
          const p2Score = parseInt(scoreArray[i * 2 + 1]) || 0;
          
          if (p1Score > p2Score) {
            if (!validateSetScore(p1Score, p2Score, config.maxScore, isSquash)) return null;
            p1SetsWon++;
          } else if (p2Score > p1Score) {
            if (!validateSetScore(p2Score, p1Score, config.maxScore, isSquash)) return null;
            p2SetsWon++;
          } else {
            // Draw set - invalid
            return null;
          }
        }
        
        // If tied at 1-1, check tiebreak
        if (p1SetsWon === 1 && p2SetsWon === 1) {
          const p1Tb = parseInt(scoreArray[4]) || 0;
          const p2Tb = parseInt(scoreArray[5]) || 0;
          
          if (p1Tb > p2Tb) {
            if (!validateTiebreakScore(p1Tb, p2Tb)) return null;
            p1SetsWon++;
          } else if (p2Tb > p1Tb) {
            if (!validateTiebreakScore(p2Tb, p1Tb)) return null;
            p2SetsWon++;
          } else {
            // Draw tiebreak - invalid
            return null;
          }
        }
      } else {
        // 3 sets (but only play 3rd if tied 1-1)
        // Check first 2 sets
        for (let i = 0; i < 2; i++) {
          const p1Score = parseInt(scoreArray[i * 2]) || 0;
          const p2Score = parseInt(scoreArray[i * 2 + 1]) || 0;
          
          if (p1Score > p2Score) {
            if (!validateSetScore(p1Score, p2Score, config.maxScore, isSquash)) return null;
            p1SetsWon++;
          } else if (p2Score > p1Score) {
            if (!validateSetScore(p2Score, p1Score, config.maxScore, isSquash)) return null;
            p2SetsWon++;
          } else {
            // Draw set - invalid
            return null;
          }
        }
        
        // If tied at 1-1, check 3rd set (indices 4 and 5)
        if (p1SetsWon === 1 && p2SetsWon === 1) {
          const p1Set3 = parseInt(scoreArray[4]) || 0;
          const p2Set3 = parseInt(scoreArray[5]) || 0;
          
          if (p1Set3 > p2Set3) {
            if (!validateSetScore(p1Set3, p2Set3, config.maxScore, isSquash)) return null;
            p1SetsWon++;
          } else if (p2Set3 > p1Set3) {
            if (!validateSetScore(p2Set3, p1Set3, config.maxScore, isSquash)) return null;
            p2SetsWon++;
          } else {
            // Draw set - invalid
            return null;
          }
        }
      }
      
      if (p1SetsWon > p2SetsWon) return player1Id;
      if (p2SetsWon > p1SetsWon) return player2Id;
      return null; // No clear winner
    }
  };

  useEffect(() => {
    loadLeague();
  }, [leagueId, isGlobalAdmin]);

  // Check active leagues limit for continuation
  useEffect(() => {
    const checkContinuationLimit = async () => {
      if (!league || !user || isGlobalAdmin) {
        // Global admins are not limited
        setCanCreateContinuation(true);
        return;
      }

      const isLeagueAdmin = league.createdBy === user.userId;
      if (!isLeagueAdmin) {
        setCanCreateContinuation(true);
        return;
      }

      try {
        const userResponse = await userAPI.getUser(user.userId);
        const userData = userResponse.data;
        const maxActiveLeagues = userData?.maxActiveLeagues ?? 1;

        const createdLeaguesResponse = await userAPI.getCreatedLeagues(user.userId);
        const createdLeagues = createdLeaguesResponse.data || [];

        const now = new Date();
        // Set time to end of day for proper date comparison
        now.setHours(23, 59, 59, 999);
        
        const activeLeagues = createdLeagues.filter(l => {
          // Exclude subleagues - only count standalone leagues and superleagues
          if (l.superLeagueId) return false;
          // Exclude the current league from the count
          if (l.leagueId === league.leagueId) return false;
          // League is active if it has no endDate or endDate is in the future (or today)
          if (!l.endDate) return true;
          const endDate = new Date(l.endDate);
          if (Number.isNaN(endDate.getTime())) return true; // Treat invalid dates as active
          // Set endDate to end of day for comparison
          endDate.setHours(23, 59, 59, 999);
          return endDate >= now;
        });

        console.log('Continuation active leagues check:', {
          userId: user.userId,
          currentLeagueId: league.leagueId,
          maxActiveLeagues,
          activeLeaguesCount: activeLeagues.length,
          activeLeagues: activeLeagues.map(l => ({ id: l.leagueId, name: l.name, endDate: l.endDate }))
        });

        if (activeLeagues.length >= maxActiveLeagues) {
          setCanCreateContinuation(false);
          setContinuationLimitMessage(`You have reached your limit of ${maxActiveLeagues} active league(s). Please wait for a league to end before creating a follow on league.`);
        } else {
          setCanCreateContinuation(true);
          setContinuationLimitMessage('');
        }
      } catch (err) {
        console.error('Failed to check continuation limit:', err);
        // On error, allow creation (backend will enforce the limit)
        setCanCreateContinuation(true);
        setContinuationLimitMessage('');
      }
    };

    checkContinuationLimit();
  }, [league, user, isGlobalAdmin]);

  const loadLeague = async () => {
    try {
      // Combined call: get league data + standings + first page of matches
      const response = await leagueAPI.getLeague(leagueId, {
        includeMatches: true,
        matchLimit: 50
      });
      const leagueData = response.data;
      setLeague(leagueData);
      
      // Set matches and pagination if included
      if (leagueData.matches) {
        setMatches(leagueData.matches);
        setMatchPagination(leagueData.matchPagination || null);
      } else {
        // If matches not included, clear state
        setMatches([]);
        setMatchPagination(null);
      }
      
      const hasAccess = leagueData.isMember || leagueData.isSubLeagueMember || leagueData.isAdmin || isGlobalAdmin;
      setShowLocationMap(!hasAccess);

      // If league is associated with a club, verify club membership/access
      if (leagueData.clubId) {
        setCheckingClubAccess(true);
        setClubAccessChecked(false);
        setClubAccessDenied(false);
        try {
          const clubRes = await clubAPI.getClub(leagueData.clubId);
          const club = clubRes.data;
          const hasClubAccess = club.isMember || club.isAdmin || club.isGlobalAdmin;
          setClubAccessDenied(!hasClubAccess);
        } catch (clubErr) {
          console.error('Failed to load club for league access check:', clubErr);
          // If we cannot verify club, deny access to be safe
          setClubAccessDenied(true);
        } finally {
          setClubAccessChecked(true);
          setCheckingClubAccess(false);
        }
      } else {
        // No associated club, normal access rules apply
        setClubAccessChecked(true);
        setClubAccessDenied(false);
      }
    } catch (err) {
      console.error('Failed to load league:', err);
      setError('Failed to load league');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreMatches = async () => {
    if (!matchPagination?.hasMore || loadingMatches) return;
    
    try {
      setLoadingMatches(true);
      const response = await leagueAPI.getLeague(leagueId, {
        includeMatches: true,
        matchLimit: 50,
        matchExclusiveStartKey: matchPagination.nextToken
      });
      
      const newMatches = response.data.matches || [];
      // Filter out any duplicates that might occur
      setMatches(prev => {
        const existingIds = new Set(prev.map(m => m.matchId));
        const uniqueNew = newMatches.filter(m => !existingIds.has(m.matchId));
        return [...prev, ...uniqueNew];
      });
      setMatchPagination(response.data.matchPagination);
    } catch (err) {
      console.error('Failed to load more matches:', err);
    } finally {
      setLoadingMatches(false);
    }
  };

  const handleExpressInterest = async () => {
    try {
      // Check if user has a level for this league's sport
      if (league?.sport && user?.userId) {
        const levelCheck = await levelsAPI.checkUserLevel(user.userId, league.sport);
        if (!levelCheck.data.hasLevel) {
          // User doesn't have a level for this sport, show questionnaire prompt
          showQuestionnairePrompt(league.sport);
          return;
        }
      }

      await leagueAPI.expressInterest(leagueId);
      showAlert('Interest expressed successfully! The league admin will review your request.', 'success');
      loadLeague();
    } catch (err) {
      console.error('Express interest error:', err);
      showAlert(err.response?.data?.error || 'Failed to express interest', 'error');
    }
  };

  const handleRemoveInterest = async () => {
    showConfirm('Are you sure you want to withdraw your interest from this league?', async () => {
      try {
        await leagueAPI.removeInterest(leagueId);
        showAlert('Interest removed successfully.', 'success');
        loadLeague();
      } catch (err) {
        console.error('Remove interest error:', err);
        showAlert(err.response?.data?.error || 'Failed to remove interest', 'error');
      }
    });
  };

  const handleCreateContinuation = async () => {
    if (!league) return;
    
    // Check if a follow on league already exists
    if (league.nextLeagueId) {
      showAlert('A follow on league already exists for this league.', 'warning');
      return;
    }
    
    // Check if user has reached their active league limit
    if (!canCreateContinuation && continuationLimitMessage) {
      showAlert(continuationLimitMessage, 'warning');
      return;
    }
    
    // Navigate to create league page with previous league data
    navigate('/leagues/create', {
      state: {
        continuationFrom: {
          leagueId: league.leagueId,
          name: league.name,
          isSuperLeague: league.isSuperLeague || false,
        }
      }
    });
  };

  const handleAddMatch = async (e) => {
    e.preventDefault();
    
    if (!matchForm.player1Id || !matchForm.player2Id) {
      showAlert('Please select both players', 'warning');
      return;
    }

    // Check if a match already exists with the same two players
    const duplicateMatch = matches.find(match => {
      const samePlayers = (
        (match.player1Id === matchForm.player1Id && match.player2Id === matchForm.player2Id) ||
        (match.player1Id === matchForm.player2Id && match.player2Id === matchForm.player1Id)
      );
      return samePlayers;
    });

    if (duplicateMatch) {
      showAlert('A match between these two players already exists in this league. Please edit or delete the existing match instead.', 'warning');
      return;
    }

    let winnerId = null;
    let scoreArray = [];

    if (matchForm.walkover) {
      // Walkover: use the selected walkover player
      if (!matchForm.walkoverPlayer) {
        showAlert('Please select which player received the walkover', 'warning');
        return;
      }
      winnerId = matchForm.walkoverPlayer;
      // For walkover, we can use a special score format or empty array
      // The backend will handle this appropriately
    } else {
      // Normal match: normalize scores and calculate winner
      scoreArray = normalizeScoreArray(matchForm.score || [], league.matchFormat);
      
      // Only validate that we can determine a winner from the scores
      winnerId = calculateWinnerFromScores(matchForm.player1Id, matchForm.player2Id, scoreArray, league.matchFormat);
      
      if (!winnerId) {
        const config = getScoreInputConfig(league.matchFormat);
        let errorMsg = 'Invalid score format. ';
        if (config.sets === 1) {
          errorMsg += `Sets to ${config.maxScore === 5 ? '4' : '6'} must be won by 2 games (minimum ${config.maxScore === 5 ? '4' : '6'}) or ${config.maxScore === 5 ? '5-4 or 5-3' : '7-6 or 7-5'}.`;
        } else {
          errorMsg += `Sets to ${config.maxScore === 5 ? '4' : '6'} must be won by 2 games (minimum ${config.maxScore === 5 ? '4' : '6'}) or ${config.maxScore === 5 ? '5-4 or 5-3' : '7-6 or 7-5'}. `;
          if (config.hasTiebreak) {
            errorMsg += 'Tiebreak must be won by 2 games with minimum 10 games.';
          }
        }
        showAlert(errorMsg, 'error');
        return;
      }
    }

    try {
      await matchAPI.addMatch({
        leagueId,
        player1Id: matchForm.player1Id,
        player2Id: matchForm.player2Id,
        winnerId,
        score: matchForm.walkover ? [] : scoreArray,
        matchDate: matchForm.matchDate,
        walkover: matchForm.walkover,
      });
      
      // Reset form with default score array
      const config = getScoreInputConfig(league.matchFormat);
      const initialScore = config.sets === 1 ? [0, 0] : [0, 0, 0, 0];
      
      // For non-admin users, preserve their player1Id
      const isUserAdmin = league?.isAdmin || isGlobalAdmin;
      const resetPlayer1Id = isUserAdmin ? '' : (user?.userId || '');
      
      setMatchForm({ 
        player1Id: resetPlayer1Id, 
        player2Id: '', 
        winnerId: '', 
        score: initialScore,
        matchDate: new Date().toISOString().split('T')[0],
        walkover: false,
        walkoverPlayer: null,
      });
      setShowAddMatch(false);
      // Reload league to get updated standings and matches (will reset to first page to show new match)
      loadLeague();
      showAlert('Match added successfully!', 'success');
    } catch (err) {
      console.error('Add match error:', err);
      showAlert(err.response?.data?.error || 'Failed to add match', 'error');
    }
  };

  const handleEditMatch = (match) => {
    // Convert old score format (string) to array if needed
    let score = match.score || [];
    if (typeof score === 'string') {
      // Try to parse old format like "6-4, 6-3"
      score = score.split(',').flatMap(s => {
        const parts = s.trim().split('-');
        return parts.length === 2 ? [parseInt(parts[0]) || 0, parseInt(parts[1]) || 0] : [];
      });
    }
    
    // Normalize score array to ensure default 0s are present
    const normalizedScore = normalizeScoreArray(Array.isArray(score) ? score : [], league?.matchFormat || 'best-of-3-sets-to-6');
    
    // For non-admin users, set player1Id to their own ID
    const isUserAdmin = league?.isAdmin || isGlobalAdmin;
    const player1Id = isUserAdmin ? match.player1Id : (user?.userId || match.player1Id);
    
    setEditingMatch({
      ...match,
      player1Id: player1Id,
      score: normalizedScore,
      matchDate: match.matchDate || new Date(match.createdAt).toISOString().split('T')[0],
      walkover: match.walkover || false,
      walkoverPlayer: match.walkover ? match.winnerId : null,
    });
    setShowAddMatch(false);
  };

  const handleUpdateMatch = async (e) => {
    e.preventDefault();
    
    if (!editingMatch.player1Id || !editingMatch.player2Id) {
      showAlert('Please select both players', 'warning');
      return;
    }

    // Check if a match already exists with the same two players (excluding the match being edited)
    const duplicateMatch = matches.find(match => {
      // Skip the match being edited
      if (match.matchId === editingMatch.matchId) {
        return false;
      }
      const samePlayers = (
        (match.player1Id === editingMatch.player1Id && match.player2Id === editingMatch.player2Id) ||
        (match.player1Id === editingMatch.player2Id && match.player2Id === editingMatch.player1Id)
      );
      return samePlayers;
    });

    if (duplicateMatch) {
      showAlert('A match between these two players already exists in this league. Please edit or delete the existing match instead.', 'warning');
      return;
    }

    let winnerId = null;
    let scoreArray = [];

    if (editingMatch.walkover) {
      if (!editingMatch.walkoverPlayer) {
        showAlert('Please select which player received the walkover', 'warning');
        return;
      }
      winnerId = editingMatch.walkoverPlayer;
    } else {
      // Normal match: normalize scores and calculate winner
      scoreArray = normalizeScoreArray(editingMatch.score || [], league.matchFormat);
      
      // Only validate that we can determine a winner from the scores
      winnerId = calculateWinnerFromScores(editingMatch.player1Id, editingMatch.player2Id, scoreArray, league.matchFormat);
      
      if (!winnerId) {
        const config = getScoreInputConfig(league.matchFormat);
        let errorMsg = 'Invalid score format. ';
        if (config.sets === 1) {
          errorMsg += `Sets to ${config.maxScore === 5 ? '4' : '6'} must be won by 2 games (minimum ${config.maxScore === 5 ? '4' : '6'}) or ${config.maxScore === 5 ? '5-4 or 5-3' : '7-6 or 7-5'}.`;
        } else {
          errorMsg += `Sets to ${config.maxScore === 5 ? '4' : '6'} must be won by 2 games (minimum ${config.maxScore === 5 ? '4' : '6'}) or ${config.maxScore === 5 ? '5-4 or 5-3' : '7-6 or 7-5'}. `;
          if (config.hasTiebreak) {
            errorMsg += 'Tiebreak must be won by 2 games with minimum 10 games.';
          }
        }
        showAlert(errorMsg, 'error');
        return;
      }
    }

    try {
      await matchAPI.updateMatch(editingMatch.matchId, {
        leagueId,
        player1Id: editingMatch.player1Id,
        player2Id: editingMatch.player2Id,
        winnerId,
        score: editingMatch.walkover ? [] : scoreArray,
        matchDate: editingMatch.matchDate,
        walkover: editingMatch.walkover,
      });
      
      setEditingMatch(null);
      // Reload league to get updated standings and matches
      loadLeague();
      showAlert('Match updated successfully!', 'success');
    } catch (err) {
      console.error('Update match error:', err);
      showAlert(err.response?.data?.error || 'Failed to update match', 'error');
    }
  };

  const handleDeleteMatch = async (matchId) => {
    showConfirm('Are you sure you want to delete this match?', async () => {
      try {
        await matchAPI.deleteMatch(matchId, leagueId);
        // Reload league to get updated standings and matches
        loadLeague();
        showAlert('Match deleted successfully!', 'success');
      } catch (err) {
        console.error('Delete match error:', err);
        showAlert(err.response?.data?.error || 'Failed to delete match', 'error');
      }
    });
  };

  const handleScoreChange = (index, player, value) => {
    const config = getScoreInputConfig(league.matchFormat);
    // For tiebreak matches, index 2 means tiebreak
    const isTiebreak = config.hasTiebreak && index === 2;
    const maxScore = isTiebreak ? (config.tiebreakMax || 15) : config.maxScore;
    let numValue = parseInt(value);
    
    // Allow 0 and empty string (which will be set to 0)
    if (value === '' || value === null || value === undefined) {
      numValue = 0;
    } else if (isNaN(numValue) || numValue < 0 || numValue > maxScore) {
      return; // Invalid input, don't update
    }

    const newScore = [...(matchForm.score || [])];
    const scoreIndex = player === 'player1' ? index * 2 : index * 2 + 1;
    
    // Ensure array is large enough
    while (newScore.length <= scoreIndex) {
      newScore.push(0);
    }
    
    newScore[scoreIndex] = numValue;
    
    setMatchForm({ ...matchForm, score: newScore });
  };

  const handleEditScoreChange = (index, player, value) => {
    const config = getScoreInputConfig(league.matchFormat);
    // For tiebreak matches, index 2 means tiebreak
    const isTiebreak = config.hasTiebreak && index === 2;
    const maxScore = isTiebreak ? (config.tiebreakMax || 15) : config.maxScore;
    let numValue = parseInt(value);
    
    // Allow 0 and empty string (which will be set to 0)
    if (value === '' || value === null || value === undefined) {
      numValue = 0;
    } else if (isNaN(numValue) || numValue < 0 || numValue > maxScore) {
      return; // Invalid input, don't update
    }

    const newScore = [...(editingMatch.score || [])];
    const scoreIndex = player === 'player1' ? index * 2 : index * 2 + 1;
    
    // Ensure array is large enough
    while (newScore.length <= scoreIndex) {
      newScore.push(0);
    }
    
    newScore[scoreIndex] = numValue;
    
    setEditingMatch({ ...editingMatch, score: newScore });
  };

  const renderScoreInputs = (formData, isEditing = false) => {
    if (!league) return null;
    
    const config = getScoreInputConfig(league.matchFormat);
    const score = formData.score || [];
    const handleChange = isEditing ? handleEditScoreChange : handleScoreChange;
    
    if (config.sets === 1) {
      // Single set: 2 inputs
      return (
        <div className="score-row">
          <div className="score-input-group">
            <label>{league.players?.find(p => p.userId === formData.player1Id)?.name || 'Player 1'}</label>
            <input
              type="number"
              min="0"
              max={config.maxScore}
              className="score-input"
              value={score[0] || 0}
              onChange={(e) => handleChange(0, 'player1', e.target.value)}
            />
          </div>
          <span className="score-separator">-</span>
          <div className="score-input-group">
            <label>{league.players?.find(p => p.userId === formData.player2Id)?.name || 'Player 2'}</label>
            <input
              type="number"
              min="0"
              max={config.maxScore}
              className="score-input"
              value={score[1] || 0}
              onChange={(e) => handleChange(0, 'player2', e.target.value)}
            />
          </div>
        </div>
      );
    } else {
      // Best of 3: Show first 2 sets, then conditionally show 3rd set or tiebreak
      const rows = [];
      
      // Always show first 2 sets
      for (let i = 0; i < 2; i++) {
        rows.push(
          <div key={`set-${i}`} className="score-row">
            <div className="score-input-group">
              <label>Set {i + 1} - {league.players?.find(p => p.userId === formData.player1Id)?.name || 'Player 1'}</label>
              <input
                type="number"
                min="0"
                max={config.maxScore}
                className="score-input"
                value={score[i * 2] || 0}
                onChange={(e) => handleChange(i, 'player1', e.target.value)}
              />
            </div>
            <span className="score-separator">-</span>
            <div className="score-input-group">
              <label>Set {i + 1} - {league.players?.find(p => p.userId === formData.player2Id)?.name || 'Player 2'}</label>
              <input
                type="number"
                min="0"
                max={config.maxScore}
                className="score-input"
                value={score[i * 2 + 1] || 0}
                onChange={(e) => handleChange(i, 'player2', e.target.value)}
              />
            </div>
          </div>
        );
      }
      
      // Check if match is tied 1-1 after first 2 sets
      const set1P1Score = parseInt(score[0]) || 0;
      const set1P2Score = parseInt(score[1]) || 0;
      const set2P1Score = parseInt(score[2]) || 0;
      const set2P2Score = parseInt(score[3]) || 0;
      
      let p1SetsWon = 0;
      let p2SetsWon = 0;
      
      if (set1P1Score > set1P2Score) p1SetsWon++;
      else if (set1P2Score > set1P1Score) p2SetsWon++;
      
      if (set2P1Score > set2P2Score) p1SetsWon++;
      else if (set2P2Score > set2P1Score) p2SetsWon++;
      
      const isTied = p1SetsWon === 1 && p2SetsWon === 1;
      
      // Only show 3rd set or tiebreak if match is tied 1-1
      if (isTied) {
        if (config.hasTiebreak) {
          // Show tiebreak
          rows.push(
            <div key="tiebreak" className="score-row">
              <div className="score-input-group">
                <label>Tiebreak - {league.players?.find(p => p.userId === formData.player1Id)?.name || 'Player 1'}</label>
                <input
                  type="number"
                  min="0"
                  max={config.tiebreakMax}
                  className="score-input"
                  value={score[4] || 0}
                  onChange={(e) => handleChange(2, 'player1', e.target.value)}
                />
              </div>
              <span className="score-separator">-</span>
              <div className="score-input-group">
                <label>Tiebreak - {league.players?.find(p => p.userId === formData.player2Id)?.name || 'Player 2'}</label>
                <input
                  type="number"
                  min="0"
                  max={config.tiebreakMax}
                  className="score-input"
                  value={score[5] || 0}
                  onChange={(e) => handleChange(2, 'player2', e.target.value)}
                />
              </div>
            </div>
          );
        } else {
          // Show 3rd set
          rows.push(
            <div key="set-2" className="score-row">
              <div className="score-input-group">
                <label>Set 3 - {league.players?.find(p => p.userId === formData.player1Id)?.name || 'Player 1'}</label>
                <input
                  type="number"
                  min="0"
                  max={config.maxScore}
                  className="score-input"
                  value={score[4] || 0}
                  onChange={(e) => handleChange(2, 'player1', e.target.value)}
                />
              </div>
              <span className="score-separator">-</span>
              <div className="score-input-group">
                <label>Set 3 - {league.players?.find(p => p.userId === formData.player2Id)?.name || 'Player 2'}</label>
                <input
                  type="number"
                  min="0"
                  max={config.maxScore}
                  className="score-input"
                  value={score[5] || 0}
                  onChange={(e) => handleChange(2, 'player2', e.target.value)}
                />
              </div>
            </div>
          );
        }
      }
      
      return <div className="score-inputs">{rows}</div>;
    }
  };

  const getAvailablePlayers = () => {
    if (!league || !user) return league?.players || [];
    return league.players || [];
  };

  const canAddMatches = () => {
    if (!league) return false;
    // Only allow adding if user is a member of this specific league (subleague) or league admin
    const isMemberOfThisLeague = !!league.isSubLeagueMember || (!league.superLeagueId && league.isMember);
    return isMemberOfThisLeague || league.isAdmin || isGlobalAdmin;
  };

  const isMatchAddingDisabled = () => {
    if (!league) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (league.startDate) {
      const startDate = new Date(league.startDate);
      startDate.setHours(0, 0, 0, 0);
      if (today < startDate) return true;
    }
    
    if (league.endDate) {
      const endDate = new Date(league.endDate);
      endDate.setHours(23, 59, 59, 999);
      if (today > endDate) return true;
    }
    
    return false;
  };

  const getMatchButtonTooltip = () => {
    if (!league) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (league.startDate) {
      const startDate = new Date(league.startDate);
      startDate.setHours(0, 0, 0, 0);
      if (today < startDate) {
        return `League hasn't started yet. Start date: ${new Date(league.startDate).toLocaleDateString()}`;
      }
    }
    
    if (league.endDate) {
      const endDate = new Date(league.endDate);
      endDate.setHours(23, 59, 59, 999);
      if (today > endDate) {
        return `League has ended. End date: ${new Date(league.endDate).toLocaleDateString()}`;
      }
    }
    
    return '';
  };

  if (loading || checkingClubAccess) {
    return (
      <div className="page-container">
        <div className="loading">Loading league...</div>
      </div>
    );
  }

  if (error || !league) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="error-message">{error || 'League not found'}</div>
          <Link to="/leagues" className="btn btn-primary">Back to Leagues</Link>
        </div>
      </div>
    );
  }

  if (league.clubId && clubAccessChecked && clubAccessDenied) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="card" style={{ maxWidth: 640, margin: '40px auto', padding: '24px' }}>
            <h1 className="page-title" style={{ marginTop: 0 }}>Access Restricted</h1>
            <p style={{ marginTop: '12px', lineHeight: 1.6 }}>
              You do not have access to leagues for this club.
            </p>
            <p style={{ marginTop: '8px', lineHeight: 1.6 }}>
              Please contact the club admin to request access if you believe you should be able to view this league.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const hasLeagueStarted = (() => {
    if (!league?.startDate) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(league.startDate);
    start.setHours(0, 0, 0, 0);
    return today >= start;
  })();

  const isUserLeagueAdmin = Boolean(league?.isAdmin);
  const isUserMember = Boolean(league?.isMember);
  const hasUserExpressedInterest = Boolean(league?.isInterested);

  const hasLeagueEnded = (() => {
    if (!league?.endDate) return false;
    const end = new Date(league.endDate);
    return !Number.isNaN(end.getTime()) && new Date() >= end;
  })();
  const hasContinuation = Boolean(league?.nextLeagueId);
  const allowContinuationButton = Boolean(league?.isAdmin || isGlobalAdmin) && !league?.superLeagueId;
  const continuationButtonDisabled = !hasLeagueEnded;
  const continuationButtonTitle = !hasLeagueEnded
    ? 'Follow on leagues can be created once this league has finished.'
    : '';

  const isApprovedMember = Boolean(league?.players && league.players.some((p) => {
    if (!p) return false;
    if (typeof p === 'string') return p === user?.userId;
    if (typeof p === 'object') return p.userId === user?.userId;
    return false;
  }));

  const canShowInterestButton =
    Boolean(user) && !isApprovedMember;

  const interestButtonDisabled = false;
  const withdrawButtonDisabled = false;
  const interestButtonTitle = '';
  const withdrawButtonTitle = '';

  return (
    <div className="page-container">
      <div className="container">
        {/* Header */}
        <div className="league-header" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {league.coverPhotoUrl && (
            <div style={{ width: 144, height: 144, borderRadius: 8, overflow: 'hidden', background: '#f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={league.coverPhotoUrl}
                alt="League cover"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
              {league.superLeagueId && (
                <Link to={`/super-leagues/${league.superLeagueId}`} className="btn btn-primary">
                  ← Back to Super League
                </Link>
              )}
              <h1 className="page-title" style={{ margin: 0 }}>{league.name}</h1>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                {canShowInterestButton && !league.superLeagueId && (
                  hasUserExpressedInterest ? (
                    <button
                      onClick={handleRemoveInterest}
                      className="btn btn-secondary"
                      disabled={withdrawButtonDisabled}
                      title={withdrawButtonTitle}
                    >
                      Withdraw Interest
                    </button>
                  ) : (
                    <button
                      onClick={handleExpressInterest}
                      className="btn btn-primary"
                      disabled={interestButtonDisabled}
                      title={interestButtonTitle}
                    >
                      Express Interest to Join
                    </button>
                  )
                )}
              {(league.isAdmin || isGlobalAdmin) && !league.superLeagueId && (
                  <>
                    {allowContinuationButton && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleCreateContinuation}
                          disabled={continuationButtonDisabled}
                          title={continuationButtonTitle}
                        >
                          Create Follow on League
                        </button>
                    )}
                  <Link to={`/leagues/${leagueId}/members`} className="btn btn-primary">
                    Edit Members
                  </Link>
                  <Link to={`/leagues/${leagueId}/edit`} className="btn btn-secondary">
                    Edit League
                  </Link>
                  </>
              )}
              </div>
            </div>
            <div className="league-meta">
              <span className="badge badge-info">{league.sport}</span>
              <span>👥 {league.players?.length || 0} players · ⭐ {league.interestedUsers?.filter(u => u.status !== 'approved').length || 0} interested</span>
              <span>🎾 {getMatchFormatDisplay(league.matchFormat)}</span>
              {league.startDate && (
                <span>📅 Start: {new Date(league.startDate).toLocaleDateString()}</span>
              )}
              {league.endDate && (
                <span>🏁 End: {new Date(league.endDate).toLocaleDateString()}</span>
              )}
              {league.createdByName && (
                <span>👤 Created by: <Link to={`/players/${league.createdBy}`}>{league.createdByName}</Link></span>
              )}
            </div>
          </div>
        </div>

        {/* Super League Navigation - Show for all users when viewing a subleague */}
        {league.superLeagueId && (
          <div className="card" style={{ backgroundColor: '#e8f5e9', border: '1px solid #4caf50', marginBottom: '20px', padding: '20px' }}>
            <div>
              <strong style={{ color: '#2e7d32', fontSize: '16px', display: 'block', marginBottom: '8px' }}>
                This is a subleague of a Super League
              </strong>
              <p style={{ margin: 0, color: '#555', fontSize: '14px' }}>
                You can express or withdraw interest, and view history and follow on leagues from the parent Super League page.
              </p>
            </div>
          </div>
        )}

        {/* Location Map */}
        {league.latitude && league.longitude && (
          <div className="card">
            <div className="collapsible-card-header">
              <h2>League Location</h2>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowLocationMap((prev) => !prev)}
              >
                {showLocationMap ? 'Hide Map' : 'Show Map'}
              </button>
            </div>
            {showLocationMap && (
              <MapPicker
                initialLocation={{ lat: parseFloat(league.latitude), lng: parseFloat(league.longitude) }}
                readOnly={true}
                height="300px"
              />
            )}
          </div>
        )}

            {/* Standings */}
            <div className="card">
              <h2>Standings</h2>
              {league.standings && league.standings.length > 0 ? (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Player</th>
                        <th>Points</th>
                        <th>Wins</th>
                        <th>Losses</th>
                        <th>Game Diff</th>
                      </tr>
                    </thead>
                    <tbody>
                      {league.standings.map((player, index) => (
                        <tr key={player.userId}>
                          <td>{index + 1}</td>
                          <td><Link to={`/players/${player.userId}`}>{player.name}</Link></td>
                          <td><strong>{player.points}</strong></td>
                          <td>{player.wins}</td>
                          <td>{player.losses}</td>
                          <td>{player.gameDiff > 0 ? '+' : ''}{player.gameDiff}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>No matches played yet.</p>
              )}
            </div>

            {/* Players */}
            <div className="card">
              <h2>Players</h2>
              <div className="players-grid">
                {league.players?.map((player) => (
                  <div key={player.userId} className="player-card">
                    <div className="player-info">
                      <h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Link to={`/players/${player.userId}`}>{player.name}</Link>
                          {user && user.userId !== player.userId && (
                            <button
                              className="message-link-button"
                              onClick={() => {
                                setSelectedChatUser({
                                  userId: player.userId,
                                  name: player.name,
                                });
                                setChatBoxOpen(true);
                              }}
                              title="Send message"
                              aria-label={`Send message to ${player.name}`}
                            >
                              💬+
                            </button>
                          )}
                        </div>
                      </h3>
                      <p>{player.email}</p>
                      <span className="badge badge-success">Level: {formatLevel(player.level)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {league.subLeagues && league.subLeagues.length > 0 && (
              <div className="card">
                <div className="collapsible-card-header">
                  <h2>Subleague Standings</h2>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowSubleagueStandings((prev) => !prev)}
                  >
                    {showSubleagueStandings ? 'Hide Standings' : 'Show Standings'}
                  </button>
                </div>
                {showSubleagueStandings && (
                  league.subLeagues.map((subLeague) => (
                    <div key={subLeague.leagueId} className="subleague-section">
                      <div className="subleague-header" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {subLeague.coverPhotoUrl && (
                          <div style={{ width: 96, height: 96, borderRadius: 6, overflow: 'hidden', background: '#f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              src={subLeague.coverPhotoUrl}
                              alt="Subleague cover"
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          </div>
                        )}
                        <h3 style={{ margin: 0 }}>{subLeague.name}</h3>
                        <Link to={`/leagues/${subLeague.leagueId}`} className="btn btn-sm btn-primary">
                          View Full League
                        </Link>
                      </div>
                      {subLeague.standings && subLeague.standings.length > 0 ? (
                        <div className="table-container">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Rank</th>
                                <th>Player</th>
                                <th>Points</th>
                                <th>Wins</th>
                                <th>Losses</th>
                                <th>Game Diff</th>
                              </tr>
                            </thead>
                            <tbody>
                              {subLeague.standings.map((player, playerIndex) => (
                                <tr key={player.userId}>
                                  <td>{playerIndex + 1}</td>
                                  <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <Link to={`/players/${player.userId}`}>{player.name}</Link>
                                      {user && user.userId !== player.userId && (
                                        <button
                                          className="message-link-button"
                                          onClick={() => {
                                            setSelectedChatUser({
                                              userId: player.userId,
                                              name: player.name,
                                              leagueId: league.leagueId,
                                              leagueName: league.name,
                                            });
                                            setChatBoxOpen(true);
                                          }}
                                          title="Send message"
                                          aria-label={`Send message to ${player.name}`}
                                        >
                                          💬+
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  <td><strong>{player.points}</strong></td>
                                  <td>{player.wins}</td>
                                  <td>{player.losses}</td>
                                  <td>{player.gameDiff > 0 ? '+' : ''}{player.gameDiff}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p>No matches played yet in this subleague.</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Matches */}
            <div className="card">
              <div className="card-header">
                <h2>Match Results</h2>
                {!editingMatch && (
                  <button 
                    onClick={() => {
                      if (showAddMatch) {
                        // For non-admin users, preserve their player1Id when canceling
                        const isUserAdmin = league?.isAdmin || isGlobalAdmin;
                        const resetPlayer1Id = isUserAdmin ? '' : (user?.userId || '');
                        
                        setMatchForm({ 
                          player1Id: resetPlayer1Id, 
                          player2Id: '', 
                          winnerId: '', 
                          score: [],
                          matchDate: new Date().toISOString().split('T')[0],
                          walkover: false,
                          walkoverPlayer: null,
                        });
                      } else {
                        // Initialize score array with default 0s based on match format
                        const config = getScoreInputConfig(league?.matchFormat || 'best-of-3-sets-to-6');
                        const initialScore = config.sets === 1 ? [0, 0] : [0, 0, 0, 0];
                        
                        // For non-admin users, prefill player1Id with their own ID
                        const isUserAdmin = league?.isAdmin || isGlobalAdmin;
                        const initialPlayer1Id = isUserAdmin ? '' : (user?.userId || '');
                        
                        setMatchForm({ 
                          player1Id: initialPlayer1Id, 
                          player2Id: '', 
                          winnerId: '', 
                          score: initialScore,
                          matchDate: new Date().toISOString().split('T')[0],
                          walkover: false,
                          walkoverPlayer: null,
                        });
                      }
                      setShowAddMatch(!showAddMatch);
                    }} 
                    className="btn btn-primary"
                    disabled={isMatchAddingDisabled() || !canAddMatches()}
                    title={getMatchButtonTooltip()}
                  >
                    {showAddMatch ? 'Cancel' : 'Add Match Result'}
                  </button>
                )}
              </div>

              {showAddMatch && (
                <form onSubmit={handleAddMatch} className="add-match-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Player 1 *</label>
                      <select
                        className="form-input"
                        value={matchForm.player1Id}
                        onChange={(e) => setMatchForm({ ...matchForm, player1Id: e.target.value })}
                        required
                        disabled={!league?.isAdmin && !isGlobalAdmin && user?.userId}
                      >
                        <option value="">Select Player 1</option>
                        {getAvailablePlayers().map((player) => (
                          <option key={player.userId} value={player.userId}>
                            {player.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Player 2 *</label>
                      <select
                        className="form-input"
                        value={matchForm.player2Id}
                        onChange={(e) => setMatchForm({ ...matchForm, player2Id: e.target.value })}
                        required
                      >
                        <option value="">Select Player 2</option>
                        {getAvailablePlayers()
                          .filter(p => {
                            // Exclude player1
                            if (p.userId === matchForm.player1Id) return false;
                            // For non-admin users, also exclude the current user from player2
                            const isUserAdmin = league?.isAdmin || isGlobalAdmin;
                            if (!isUserAdmin && p.userId === user?.userId) return false;
                            return true;
                          })
                          .map((player) => (
                            <option key={player.userId} value={player.userId}>
                              {player.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Match Date *</label>
                      <input
                        type="date"
                        className="form-input"
                        value={matchForm.matchDate}
                        onChange={handleMatchDateChange}
                        required
                      />
                    </div>
                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                      <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>&nbsp;</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '10px', width: 'fit-content' }}>
                        <input
                          type="checkbox"
                          id="walkover-checkbox"
                          checked={matchForm.walkover}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setMatchForm({ 
                                ...matchForm, 
                                walkover: true,
                                walkoverPlayer: null,
                                score: [],
                              });
                            } else {
                              // Initialize score with default 0s when walkover is unchecked
                              const config = getScoreInputConfig(league?.matchFormat || 'best-of-3-sets-to-6');
                              const initialScore = config.sets === 1 ? [0, 0] : [0, 0, 0, 0];
                              setMatchForm({ 
                                ...matchForm, 
                                walkover: false,
                                walkoverPlayer: null,
                                score: normalizeScoreArray(matchForm.score || [], league?.matchFormat || 'best-of-3-sets-to-6') || initialScore,
                              });
                            }
                          }}
                          style={{ margin: 0, cursor: 'pointer', flexShrink: 0 }}
                        />
                        <label htmlFor="walkover-checkbox" style={{ margin: 0, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          Walkover
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    {matchForm.walkover ? (
                      <div className="form-group">
                        <label className="form-label">Player taking the win *</label>
                        <select
                          className="form-input"
                          value={matchForm.walkoverPlayer || ''}
                          onChange={(e) => setMatchForm({ ...matchForm, walkoverPlayer: e.target.value })}
                          required
                        >
                          <option value="">Select Player</option>
                          {matchForm.player1Id && (
                            <option value={matchForm.player1Id}>
                              {league.players?.find(p => p.userId === matchForm.player1Id)?.name}
                            </option>
                          )}
                          {matchForm.player2Id && (
                            <option value={matchForm.player2Id}>
                              {league.players?.find(p => p.userId === matchForm.player2Id)?.name}
                            </option>
                          )}
                        </select>
                      </div>
                    ) : (
                      <>
                        <label className="form-label">Score *</label>
                        {matchForm.player1Id && matchForm.player2Id && renderScoreInputs(matchForm)}
                      </>
                    )}
                  </div>

                  <button type="submit" className="btn btn-success">
                    Add Match
                  </button>
                </form>
              )}

              {editingMatch && (
                <form onSubmit={handleUpdateMatch} className="add-match-form">
                  <h3>Edit Match</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Player 1 *</label>
                      <select
                        className="form-input"
                        value={editingMatch.player1Id}
                        onChange={(e) => setEditingMatch({ ...editingMatch, player1Id: e.target.value })}
                        required
                        disabled={true}
                      >
                        {getAvailablePlayers().map((player) => (
                          <option key={player.userId} value={player.userId}>
                            {player.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Player 2 *</label>
                      <select
                        className="form-input"
                        value={editingMatch.player2Id}
                        onChange={(e) => setEditingMatch({ ...editingMatch, player2Id: e.target.value })}
                        required
                        disabled={true}
                      >
                        {getAvailablePlayers()
                          .filter(p => {
                            // Exclude player1
                            if (p.userId === editingMatch.player1Id) return false;
                            // For non-admin users, also exclude the current user from player2
                            const isUserAdmin = league?.isAdmin || isGlobalAdmin;
                            if (!isUserAdmin && p.userId === user?.userId) return false;
                            return true;
                          })
                          .map((player) => (
                            <option key={player.userId} value={player.userId}>
                              {player.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Match Date *</label>
                      <input
                        type="date"
                        className="form-input"
                        value={editingMatch.matchDate}
                        onChange={handleEditingMatchDateChange}
                        required
                      />
                    </div>
                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                      <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>&nbsp;</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '10px', width: 'fit-content' }}>
                        <input
                          type="checkbox"
                          id="walkover-checkbox-edit"
                          checked={editingMatch.walkover || false}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditingMatch({ 
                                ...editingMatch, 
                                walkover: true,
                                walkoverPlayer: editingMatch.walkoverPlayer || editingMatch.winnerId || null,
                                score: [],
                              });
                            } else {
                              // Initialize score with default 0s when walkover is unchecked
                              const config = getScoreInputConfig(league?.matchFormat || 'best-of-3-sets-to-6');
                              const initialScore = config.sets === 1 ? [0, 0] : [0, 0, 0, 0];
                              setEditingMatch({ 
                                ...editingMatch, 
                                walkover: false,
                                walkoverPlayer: null,
                                score: normalizeScoreArray(editingMatch.score || [], league?.matchFormat || 'best-of-3-sets-to-6') || initialScore,
                              });
                            }
                          }}
                          style={{ margin: 0, cursor: 'pointer', flexShrink: 0 }}
                        />
                        <label htmlFor="walkover-checkbox-edit" style={{ margin: 0, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          Walkover
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    {editingMatch.walkover ? (
                      <div className="form-group">
                        <label className="form-label">Player taking the win *</label>
                        <select
                          className="form-input"
                          value={editingMatch.walkoverPlayer || editingMatch.winnerId || ''}
                          onChange={(e) => setEditingMatch({ ...editingMatch, walkoverPlayer: e.target.value })}
                          required
                        >
                          <option value="">Select Player</option>
                          {editingMatch.player1Id && (
                            <option value={editingMatch.player1Id}>
                              {league.players?.find(p => p.userId === editingMatch.player1Id)?.name}
                            </option>
                          )}
                          {editingMatch.player2Id && (
                            <option value={editingMatch.player2Id}>
                              {league.players?.find(p => p.userId === editingMatch.player2Id)?.name}
                            </option>
                          )}
                        </select>
                      </div>
                    ) : (
                      <>
                        <label className="form-label">Score *</label>
                        {editingMatch.player1Id && editingMatch.player2Id && renderScoreInputs(editingMatch, true)}
                      </>
                    )}
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-success">
                      Update Match
                    </button>
                    <button type="button" onClick={() => setEditingMatch(null)} className="btn btn-secondary">
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {matches.length > 0 ? (
                <>
                  <div className="matches-list">
                    {matches.map((match) => {
                    const isPlayer = user && (match.player1Id === user.userId || match.player2Id === user.userId);
                    // Edit allowed only if admin or involved AND user is member of this league (for general users)
                    const isMemberOfThisLeague = !!league.isSubLeagueMember || (!league.superLeagueId && league.isMember);
                    const canEditDuringSeason = league.isAdmin || isGlobalAdmin || (isPlayer && isMemberOfThisLeague);
                    const canDeleteDuringSeason = league.isAdmin || isGlobalAdmin;
                    const timeWindowDisabled = isMatchAddingDisabled();
                    const seasonOver = (() => {
                      if (!league?.endDate) return false;
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const endDate = new Date(league.endDate);
                      endDate.setHours(23, 59, 59, 999);
                      return today > endDate;
                    })();
                    const matchActionDisabled = timeWindowDisabled || seasonOver;
                    const matchActionTooltip = matchActionDisabled ? getMatchButtonTooltip() : '';
                    
                    // Format score display
                    let scoreDisplay = '';
                    if (match.score) {
                      if (Array.isArray(match.score)) {
                        const config = getScoreInputConfig(league.matchFormat);
                        if (config.sets === 1) {
                          scoreDisplay = `${match.score[0] || 0}-${match.score[1] || 0}`;
                        } else {
                          const sets = [];
                          // Check if match was decided in 2 sets
                          const set1P1Score = parseInt(match.score[0]) || 0;
                          const set1P2Score = parseInt(match.score[1]) || 0;
                          const set2P1Score = parseInt(match.score[2]) || 0;
                          const set2P2Score = parseInt(match.score[3]) || 0;
                          
                          const set1Winner = set1P1Score > set1P2Score ? 1 : (set1P2Score > set1P1Score ? 2 : 0);
                          const set2Winner = set2P1Score > set2P2Score ? 1 : (set2P2Score > set2P1Score ? 2 : 0);
                          
                          // If same player won both sets, match was decided in 2 sets
                          const decidedInTwoSets = set1Winner !== 0 && set2Winner !== 0 && set1Winner === set2Winner;
                          
                          if (decidedInTwoSets) {
                            // Only show the 2 sets that were played
                            sets.push(`${set1P1Score}-${set1P2Score}`);
                            sets.push(`${set2P1Score}-${set2P2Score}`);
                          } else {
                            // Match went to 3rd set or tiebreak
                            const setsToShow = config.hasTiebreak ? 2 : 3;
                            for (let i = 0; i < setsToShow; i++) {
                              sets.push(`${match.score[i * 2] || 0}-${match.score[i * 2 + 1] || 0}`);
                            }
                            if (config.hasTiebreak && match.score.length >= 6) {
                              sets.push(`TB: ${match.score[4] || 0}-${match.score[5] || 0}`);
                            }
                          }
                          scoreDisplay = sets.join(', ');
                        }
                      } else {
                        scoreDisplay = match.score;
                      }
                    }
                    
                    return (
                      <div key={match.matchId} className="match-item">
                        <div className="match-content">
                          <div className="match-players">
                            <span className={match.winnerId === match.player1Id ? 'winner' : ''}>
                              <Link to={`/players/${match.player1Id}`}>{match.player1Name}</Link>
                            </span>
                            <span className="vs">vs</span>
                            <span className={match.winnerId === match.player2Id ? 'winner' : ''}>
                              <Link to={`/players/${match.player2Id}`}>{match.player2Name}</Link>
                            </span>
                          </div>
                          {match.walkover ? (
                            <div className="match-score" style={{ color: '#dc2626', fontWeight: 'bold' }}>Walkover</div>
                          ) : (
                            scoreDisplay && <div className="match-score">{scoreDisplay}</div>
                          )}
                          <div className="match-winner">Winner: {match.winnerName}</div>
                          {match.matchDate && (
                            <div className="match-date">Date: {new Date(match.matchDate).toLocaleDateString()}</div>
                          )}
                        </div>
                        {(canEditDuringSeason || canDeleteDuringSeason) && (
                          <div className="match-actions">
                            {canEditDuringSeason && (
                              <button
                                onClick={() => handleEditMatch(match)}
                                className="btn btn-sm btn-secondary"
                                disabled={matchActionDisabled}
                                title={matchActionTooltip}
                              >
                                Edit
                              </button>
                            )}
                            {canDeleteDuringSeason && (
                              <button
                                onClick={() => handleDeleteMatch(match.matchId)}
                                className="btn btn-sm btn-danger"
                                disabled={matchActionDisabled}
                                title={matchActionTooltip}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {matchPagination?.hasMore && (
                  <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button
                      onClick={loadMoreMatches}
                      className="btn btn-primary"
                      disabled={loadingMatches}
                    >
                      {loadingMatches ? 'Loading...' : 'Load More Matches'}
                    </button>
                    {matchPagination.totalMatches && (
                      <p style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
                        Showing {matches.length} of {matchPagination.totalMatches} matches
                      </p>
                    )}
                  </div>
                )}
                </>
              ) : (
                <p>No match results yet.</p>
              )}
            </div>
        {!league?.superLeagueId && (league?.previousLeagueId || league?.nextLeagueId || (Array.isArray(league?.continuationSeries) && league.continuationSeries.length > 0)) && (
          <div className="card">
            <div className="collapsible-card-header">
              <h2>History & Follow on Leagues</h2>
              {Array.isArray(league.continuationSeries) && league.continuationSeries.length > 0 && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowContinuationHistory((prev) => !prev)}
                >
                  {showContinuationHistory ? 'Hide Full History' : 'Show Full History'}
                </button>
              )}
            </div>
            
            {/* Quick view: Previous and Next leagues - only show when full history is hidden */}
            {!showContinuationHistory && (league?.previousLeagueId || league?.nextLeagueId) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {league.previousLeagueId && (() => {
                  const prevLeague = Array.isArray(league.continuationSeries) 
                    ? league.continuationSeries.find(item => item.leagueId === league.previousLeagueId)
                    : null;
                  if (!prevLeague) return null;
                  const targetPath = prevLeague.isSuperLeague
                    ? `/super-leagues/${prevLeague.leagueId}`
                    : `/leagues/${prevLeague.leagueId}`;
                  return (
                    <div
                      style={{
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb',
                        borderRadius: '10px',
                        padding: '12px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ flex: '1 1 auto', minWidth: 200 }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>
                          Previous League
                        </div>
                        <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>
                          {prevLeague.name}
                        </div>
                        <div style={{ fontSize: 14, color: '#4b5563' }}>
                          <div>
                            {formatDateDisplay(prevLeague.startDate)} – {formatDateDisplay(prevLeague.endDate)}
                          </div>
                        </div>
                      </div>
                      <Link to={targetPath} className="btn btn-sm btn-secondary">
                        View League
                      </Link>
                    </div>
                  );
                })()}
                
                {league.nextLeagueId && (() => {
                  const nextLeague = Array.isArray(league.continuationSeries) 
                    ? league.continuationSeries.find(item => item.leagueId === league.nextLeagueId)
                    : null;
                  if (!nextLeague) return null;
                  const targetPath = nextLeague.isSuperLeague
                    ? `/super-leagues/${nextLeague.leagueId}`
                    : `/leagues/${nextLeague.leagueId}`;
                  return (
                    <div
                      style={{
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb',
                        borderRadius: '10px',
                        padding: '12px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ flex: '1 1 auto', minWidth: 200 }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>
                          Next League
                        </div>
                        <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>
                          {nextLeague.name}
                        </div>
                        <div style={{ fontSize: 14, color: '#4b5563' }}>
                          <div>
                            {formatDateDisplay(nextLeague.startDate)} – {formatDateDisplay(nextLeague.endDate)}
                          </div>
                        </div>
                      </div>
                      <Link to={targetPath} className="btn btn-sm btn-secondary">
                        View League
                      </Link>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Full history view */}
            {showContinuationHistory && Array.isArray(league.continuationSeries) && league.continuationSeries.length > 0 && (() => {
              // Sort by start date (latest first)
              const sortedSeries = [...league.continuationSeries].sort((a, b) => {
                const dateA = a.startDate ? new Date(a.startDate).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
                const dateB = b.startDate ? new Date(b.startDate).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
                return dateB - dateA; // Descending order (latest first)
              });
              
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {sortedSeries.map((item) => {
                    const isCurrentLeague = item.leagueId === leagueId;
                    const isPreviousLeague = item.leagueId === league.previousLeagueId;
                    const isNextLeague = item.leagueId === league.nextLeagueId;
                    const statusLabel = item.status
                      ? item.status.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                      : 'Unknown';
                    const targetPath = item.isSuperLeague
                      ? `/super-leagues/${item.leagueId}`
                      : `/leagues/${item.leagueId}`;
                    return (
                      <div
                        key={item.leagueId}
                        style={{
                          border: isCurrentLeague ? '1px solid #2563eb' : '1px solid #e5e7eb',
                          backgroundColor: isCurrentLeague ? '#eff6ff' : '#ffffff',
                          borderRadius: '10px',
                          padding: '12px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '12px',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div style={{ flex: '1 1 auto', minWidth: 200 }}>
                          {(isPreviousLeague || isNextLeague) && (
                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>
                              {isPreviousLeague ? 'Previous League' : 'Next League'}
                            </div>
                          )}
                          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>
                            {item.name}
                            {isCurrentLeague && (
                              <span className="badge badge-info" style={{ marginLeft: 8 }}>
                                Current League
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 14, color: '#4b5563' }}>
                            <div>
                              {formatDateDisplay(item.startDate)} – {formatDateDisplay(item.endDate)}
                            </div>
                            <div>Status: {statusLabel}</div>
                          </div>
                        </div>
                        <Link to={targetPath} className="btn btn-sm btn-secondary">
                          View League
                        </Link>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {chatBoxOpen && selectedChatUser && (
        <ChatBox
          otherUserId={selectedChatUser.userId}
          otherUserName={selectedChatUser.name}
          onClose={() => {
            setChatBoxOpen(false);
            setSelectedChatUser(null);
          }}
        />
      )}
    </div>
  );
};

export default LeaguePage;

